import { Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { DatePipe } from '@angular/common';

interface TimeAgoPipeConfig {
  amPmFormatWhenCloseToNow?: boolean;
  displayNowLessOneMinute?: boolean;
}

@Pipe({
  name: 'timeAgo',
})
export class TimeAgoPipe implements PipeTransform {
  private _datePipe = new DatePipe('en-US');

  constructor(private _translocoService: TranslocoService) {}

  transform(value: Date | string, config?: TimeAgoPipeConfig): string {
    if (typeof value == 'string') {
      // Parse the ISO string as-is. The backend returns UTC timestamps with a trailing "Z";
      // stripping it made `new Date(...)` read the value as local time, which offset the
      // relative time by the user's timezone (e.g. a brand-new comment showing "Há 5 horas").
      value = new Date(value);
    }
    const unit = this.getUnitBySecondsPassed(value);
    if ((config?.amPmFormatWhenCloseToNow ?? true) && unit.unit === TimeUnity.second && unit.value < 60) {
      return this._datePipe.transform(value, 'hh:mm a').toUpperCase();
    }

    const term = this.getTermByValue(unit.unit, unit.value, config);
    return term.replace('{0}', unit.value.toString());
  }

  private getUnitBySecondsPassed(date: Date): { unit: TimeUnity; value: number } {
    const now = new Date();
    const seconds = Math.max(Math.floor((now.getTime() - date.getTime()) / 1000), 0);

    if (seconds < 60) {
      return { unit: TimeUnity.second, value: seconds };
    } else if (seconds < 120) {
      // Até 2 minutos
      return { unit: TimeUnity.minute, value: 1 };
    } else if (seconds < 3600) {
      // Até 1 hora
      return { unit: TimeUnity.minute, value: Math.floor(seconds / 60) };
    } else if (seconds < 7200) {
      // Até 2 horas
      return { unit: TimeUnity.hour, value: 1 };
    } else if (seconds < 86400) {
      // Até 24hrs
      return { unit: TimeUnity.hour, value: Math.floor(seconds / 3600) };
    } else if (seconds < 172800) {
      // Até 47 hrs
      return { unit: TimeUnity.day, value: 1 };
    } else if (seconds < 604800) {
      // Até 7 dias
      return { unit: TimeUnity.day, value: Math.floor(seconds / 86400) };
    } else if (seconds < 1209600) {
      // Até 14 dias
      return { unit: TimeUnity.week, value: 1 };
    } else if (seconds < 2678400) {
      // Até 31 dias
      return { unit: TimeUnity.week, value: Math.floor(seconds / 604800) };
    } else {
      const monthsAndYears = this.getMonthsAndYearsDifference(date, now);
      if (monthsAndYears.years > 0) {
        return { unit: TimeUnity.year, value: monthsAndYears.years };
      } else if (monthsAndYears.months > 0) {
        return { unit: TimeUnity.month, value: monthsAndYears.months };
      } else {
        return { unit: TimeUnity.year, value: monthsAndYears.years };
      }
    }
  }

  private getTermByValue(timeUnity: TimeUnity, value: number, config?: TimeAgoPipeConfig) {
    const translate = this._translocoService.translate.bind(this._translocoService);
    switch (timeUnity) {
      case TimeUnity.second:
        return config?.displayNowLessOneMinute && value < 60 ? translate('agora') : translate(`Há {0} ${value > 1 ? 'segundos' : 'segundo'}`);
      case TimeUnity.minute:
        return translate(`Há {0} ${value > 1 ? 'minutos' : 'minuto'}`);
      case TimeUnity.hour:
        return translate(`Há {0} ${value > 1 ? 'horas' : 'hora'}`);
      case TimeUnity.day:
        return translate(`Há {0} ${value > 1 ? 'dias' : 'dia'}`);
      case TimeUnity.week:
        return translate(`Há {0} ${value > 1 ? 'semanas' : 'semana'}`);
      case TimeUnity.month:
        return translate(`Há {0} ${value > 1 ? 'meses' : 'mês'}`);
      case TimeUnity.year:
        return translate(`Há {0} ${value > 1 ? 'anos' : 'ano'}`);
    }
  }

  private getMonthsAndYearsDifference(date1: Date, date2: Date): { months: number; years: number } {
    let diffYears = date2.getFullYear() - date1.getFullYear();
    let diffMonths = date2.getMonth() - date1.getMonth();

    if (date1.getFullYear() < date2.getFullYear()) {
      diffMonths--;
      if (diffMonths < 0) {
        diffMonths += 12;
        diffYears--;
      }
    } else if (diffMonths < 0) {
      diffYears--;
      diffMonths += 12;
    }

    return { months: diffMonths, years: diffYears };
  }
}

enum TimeUnity {
  second,
  minute,
  hour,
  day,
  week,
  month,
  year,
}

